import React, { useCallback, useContext, useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  DollarSign,
  TrendingUp,
  Wallet,
  Briefcase,
  Home,
  Car,
  Heart,
  ShoppingCart,
  Wifi,
  Coffee,
  Activity,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  FileText,
  PieChart,
  ArrowRight,
} from "lucide-react";
import LayoutAuth from "../../blocks/layout-auth/layout-auth";
import { ReactComponent as GuyChart } from "../../assets/svg/guy_chart.svg";
import { Donut } from "britecharts-react";
import { useForm, useWatch } from "react-hook-form";
import Select from "react-select";
import { MainStore } from "../../stores/Main";
import API from "../../utils/api";
import { Redirect, useHistory } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { trackQuestionnaireSubmitted } from "../../utils/posthogTracking";

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const CleanNumber = (num) => {
  if (num === undefined) return 0;
  num = num.replaceAll("$", "");
  num = num.replaceAll(",", "");
  num = Number(num);
  return num;
};

const Sum = (obj) => {
  let sum = 0;
  for (let el in obj) {
    if (obj.hasOwnProperty(el)) {
      let cleanAmount = CleanNumber(obj[el]);
      if (!isNaN(cleanAmount)) sum += cleanAmount;
    }
  }
  return sum;
};

const Questionnaire = (props) => {
  const history = useHistory();
  const {
    register,
    control,
    getValues,
    setValue,
    handleSubmit,
  } = useForm();
  const [incomeTotal, setIncomeTotal] = useState(0);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [availableFunds, setAvailableFunds] = useState(0);
  const [expenseRatio, setExpenseRatio] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [chartData, setChartData] = useState([]);
  const posthog = usePostHog();
  const startTimeRef = useRef(Date.now());
  const {
    isAuthenticated,
    agencyLinked,
    acceptedDisclosures,
    handleQuestionnaire,
    questionnaire,
    sentimentQuestionnaire,
    appConfig,
  } = useContext(MainStore);

  if (!isAuthenticated) {
    props.history.push("/");
  }

  const [formStatus, setFormStatus] = useState(0);
  const onSubmit = async (data) => {
    if (!checkReq()) {
      return;
    }
    setLoading(true);
    const request = data;
    request.income.monthly_household = CleanNumber(
      request.income.monthly_household
    );
    if (request.income.benefit_type) {
      request.income.benefit_type = Number(request.income.benefit_type);
    }
    if (request.income.benefit_amount) {
      request.income.benefit_amount = CleanNumber(request.income.benefit_amount);
    }
    for (let expense in request.expenses) {
      request.expenses[expense] = CleanNumber(request.expenses[expense]);
    }
    request.income.employed = request.income.employed === "yes" ? true : false;
    try {
      const response = await API.post("/financial-questionnaire", request);
      
      // Track questionnaire submission with completion time
      const completionTime = Math.round((Date.now() - startTimeRef.current) / 1000);
      trackQuestionnaireSubmitted("financial", completionTime);
      
      handleQuestionnaire();
      if (
        appConfig.onboarding_sentiment !== "disabled" &&
        !sentimentQuestionnaire
      ) {
        // Forward the return-path state (the wellness gate sends
        // { from: "/wellness-coach" }) so the sentiment page can finish
        // the financial -> sentiment -> wellness flow.
        props.history.push({
          pathname: "/sentiment-questionnaire",
          state: props.history.location.state,
        });
      } else if (props.history.location.state?.from) {
        props.history.push("/dashboard/wellness-coach");
      } else {
        props.history.push("/");
      }
    } catch (e) {}
    setLoading(false);
  };

  const employed = useWatch({
    control,
    name: "income.employed",
    defaultValue: "yes",
  });

  const calculateTotals = () => {
    // Calculate income
    const income = getValues("income.monthly_household");
    const total = CleanNumber(income);
    setIncomeTotal(total);

    // Calculate expenses
    const expenseCat = getValues("expenses");
    const expenses = Sum(expenseCat);
    setExpenseTotal(expenses);
    setAvailableFunds(total - expenses);
    setExpenseRatio(expenses / total);

    // Calculate chart
    let data = [];
    for (let el in expenseCat) {
      let catName = el.replace("_", " ");
      let catValue = CleanNumber(expenseCat[el]);
      if (catValue !== 0) {
        data.push({
          id: el,
          name: catName,
          quantity: (catValue / expenses) * 100,
        });
      }
    }

    setChartData(data);
  };

  const checkReq = () => {
    const income = getValues("income");
    console.log(income);
    if (income.employed === "yes") {
      if (
        !income.monthly_household ||
        !income.employer ||
        income.pay_frequency === undefined
      ) {
        setError(true);
        window.scrollTo(0, 0);
        return false;
      }
    } else {
      if (
        !income.monthly_household ||
        !income.benefit_type ||
        !income.benefit_amount
      ) {
        setError(true);
        window.scrollTo(0, 0);
        return false;
      }
    }
    setError(false);
    return true;
  };

  const doSkip = useCallback(() => {
    posthog?.capture("skip_financial_questionnaire");
    if (appConfig.onboarding_payment_options !== "disabled") {
      history.push("/payment-options");
    }
  }, [appConfig, history]);

  React.useLayoutEffect(() => {
    if (questionnaire) {
      setFormStatus(2);
    }
  }, [questionnaire]);

  if (isAuthenticated && agencyLinked && !acceptedDisclosures) {
    return (
      <Redirect
        to={{
          pathname: "/dashboard/disclosures",
        }}
      />
    );
  }

  if (questionnaire) {
    return (
      <Redirect
        to={{
          pathname: "/",
        }}
      />
    );
  }

  // Dark theme select styles
  const darkSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused ? "2px solid #3b82f6" : "2px solid #4b5563",
      borderRadius: "12px",
      backgroundColor: "rgba(31, 41, 55, 0.9)",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": {
        borderColor: "#3b82f6",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#e5e7eb",
      fontWeight: "500",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "12px",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
      backgroundColor: "#1f2937",
      border: "1px solid #374151",
      overflow: "hidden",
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
          ? "#374151"
          : "#1f2937",
      color: state.isSelected ? "white" : "#e5e7eb",
      cursor: "pointer",
      margin: "0",
      "&:active": {
        backgroundColor: "#3b82f6",
      },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#9ca3af",
      "&:hover": {
        color: "#e5e7eb",
      },
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: "#4b5563",
    }),
  };

  const expenseFields = [
    {
      name: "loans_mortgages",
      label: "Loans and mortgages",
      hint: "(eg: car loan, home loan)",
      icon: Home,
    },
    { name: "travel", label: "Travel", icon: Car },
    { name: "rent", label: "Rent", icon: Home },
    { name: "doctor", label: "Doctor visits", icon: Heart },
    {
      name: "utilities",
      label: "Utilities",
      hint: "(eg: water, electricity, gas)",
      icon: Activity,
    },
    { name: "medicine", label: "Medicine", icon: Heart },
    {
      name: "auto_expenses",
      label: "Automobile",
      hint: "(eg: gas, maintenance, repairs)",
      icon: Car,
    },
    {
      name: "entertainment_recreation",
      label: "Entertainment / Recreation",
      hint: "(eg: movies, concerts, casinos, sports, parks)",
      icon: Activity,
    },
    {
      name: "groceries_supermarkets",
      label: "Groceries and supermarkets",
      icon: ShoppingCart,
    },
    {
      name: "shopping",
      label: "Shopping",
      hint: "(eg: department stores, outlets, jewelry)",
      icon: ShoppingCart,
    },
    {
      name: "health_insurance",
      label: "Health Insurance",
      hint: "(leave blank if already withheld in paycheck)",
      icon: Heart,
    },
    {
      name: "personal_care",
      label: "Personal care",
      hint: "(eg: spas, hair salons, massages)",
      icon: Heart,
    },
    {
      name: "eating_out",
      label: "Eating out",
      hint: "(eg: restaurants, bars, nightlife)",
      icon: Coffee,
    },
    { name: "internet_tv", label: "Internet and TV", icon: Wifi },
    { name: "daycare", label: "Daycare", icon: Home },
    {
      name: "streaming",
      label: "Streaming services",
      hint: "(eg: Netflix, Amazon, Hulu)",
      icon: Wifi,
    },
  ];

  return (
    <div className="wrapper__inner">
      <div className="main">
        {formStatus === 0 && (
          <LayoutAuth showContainer={false} showIllustration={false}>
            <div className="relative max-w-6xl mx-auto px-6 py-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-2xl mx-auto"
            >
              <div className="mb-8">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg mb-6">
                    <DollarSign size={48} className="text-white" />
                  </div>
                  <h1 className="text-4xl font-bold text-white mb-4">
                    Financial Questionnaire
                  </h1>
                  <p className="text-gray-300 text-lg max-w-xl mx-auto">
                    Take control of your financial future with personalized
                    insights
                  </p>
                </motion.div>

                {/* Information Cards */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="mt-8 space-y-4"
                >
                  <div className="bg-blue-900/20 border border-blue-700/50 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <PieChart
                        size={20}
                        className="text-blue-400 mt-0.5 flex-shrink-0"
                      />
                      <div>
                        <h3 className="text-blue-200 font-semibold mb-1">
                          Why Complete This Questionnaire?
                        </h3>
                        <p className="text-blue-300/80 text-sm">
                          Understanding your income and expenses helps us create
                          a realistic budget, identify savings opportunities,
                          and develop a personalized plan to achieve your
                          financial goals faster.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* <div className="bg-gray-700/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <TrendingUp size={20} className="text-green-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="text-gray-200 font-semibold mb-1">What You'll Get</h3>
                        <ul className="text-gray-300 text-sm space-y-1">
                          <li className="flex items-start gap-2">
                            <CheckCircle size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                            <span>Personalized budget recommendations based on your unique situation</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                            <span>Insights into your spending patterns and potential savings areas</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                            <span>Custom debt payoff strategies tailored to your income</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div> */}

                  <div className="bg-gray-700/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle
                        size={20}
                        className="text-yellow-400 mt-0.5 flex-shrink-0"
                      />
                      <div>
                        <h3 className="text-yellow-200 font-semibold mb-1">
                          Your Privacy Matters
                        </h3>
                        <p className="text-yellow-300/80 text-sm">
                          Your financial information is encrypted and secure. We
                          never share your personal data with third parties
                          without your explicit consent.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-8">
                <h2 className="text-xl font-semibold text-white mb-2">
                  Ready to get started?
                </h2>
                <p className="text-gray-400 text-sm mb-6">
                  We'll ask a few questions about your income and expenses.
                </p>
                <div className="space-y-6">
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    onClick={() => setFormStatus(1)}
                    className="w-full flex items-start gap-4 p-4 bg-gray-700/30 hover:bg-gray-700/50 rounded-xl border-2 border-gray-600 hover:border-gray-500 transition-all text-left"
                  >
                    <FileText
                      size={24}
                      className="text-gray-400 mt-1 flex-shrink-0"
                    />
                    <div>
                      <h3 className="text-white font-semibold mb-1">
                        Begin Questionnaire
                      </h3>
                      <p className="text-gray-300 text-sm">
                        It takes about 15 minutes to complete.
                      </p>
                    </div>
                    <ChevronRight
                      size={20}
                      className="text-gray-400 mt-1 ml-auto flex-shrink-0"
                    />
                  </motion.button>
                </div>

                {["skip", "skip-login"].includes(
                  appConfig.onboarding_financials
                ) && (
                  <button
                    onClick={doSkip}
                    className="w-full mt-4 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl font-semibold transition-all"
                  >
                    Maybe Later
                  </button>
                )}
              </div>
            </motion.div>
            </div>
          </LayoutAuth>
        )}

        {formStatus === 1 && (
          <LayoutAuth showContainer={false} showIllustration={false}>
            <div className="relative max-w-6xl mx-auto px-6 py-8">
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full"
                onBlur={calculateTotals}
              >
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 mb-8 text-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold mb-2">
                      Financial Questionnaire
                    </h1>
                    <p className="text-blue-100">
                      Freede from worry, Freede from stress, Freede from debt.
                    </p>
                  </div>
                  <GuyChart className="h-32 opacity-80" />
                </div>
              </motion.div>

              {/* Income Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-8"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <TrendingUp size={20} className="text-green-400" />
                    </div>
                    <h2 className="text-2xl font-semibold text-white">
                      Income
                    </h2>
                  </div>
                  {error && (
                    <div className="flex items-center gap-2 text-red-400">
                      <AlertCircle size={16} />
                      <span className="text-sm">* Required fields</span>
                    </div>
                  )}
                </div>

                <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700">
                    <span className="text-gray-400">Total Income:</span>
                    <span
                      className={`text-2xl font-bold ${incomeTotal >= 0 ? "text-green-400" : "text-red-400"}`}
                    >
                      {formatter.format(incomeTotal)}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-300 mb-2 text-sm font-medium">
                        Monthly household take home pay *
                      </label>
                      <input
                        type="text"
                        placeholder="$0.00"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        {...register("income.monthly_household", {
                          required: true,
                        })}
                      />
                    </div>

                    {employed === "yes" && (
                      <div>
                        <label className="block text-gray-300 mb-2 text-sm font-medium">
                          Who is your employer? *
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          {...register("income.employer", { required: true })}
                        />
                      </div>
                    )}

                    {employed === "no" && (
                      <div>
                        <label className="block text-gray-300 mb-2 text-sm font-medium">
                          Disability or other benefits? *
                        </label>
                        <input
                          type="text"
                          placeholder="$0.00"
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          {...register("income.benefit_amount")}
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label className="block text-gray-300 mb-3 text-sm font-medium">
                        Are you employed? *
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            {...register("income.employed")}
                            value="yes"
                            defaultChecked
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-gray-200">Yes</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            {...register("income.employed")}
                            value="no"
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-gray-200">No</span>
                        </label>
                      </div>
                    </div>

                    {employed === "yes" && (
                      <div>
                        <label className="block text-gray-300 mb-2 text-sm font-medium">
                          Pay frequency? *
                        </label>
                        <Select
                          options={[
                            { label: "Daily", value: 0 },
                            { label: "Weekly", value: 1 },
                            { label: "Bi-Weekly", value: 2 },
                            { label: "Semi-monthly", value: 3 },
                            { label: "Monthly", value: 4 },
                            { label: "Quarterly", value: 5 },
                            { label: "Semi-annually", value: 6 },
                            { label: "Annually", value: 7 },
                          ]}
                          onChange={(e) =>
                            setValue("income.pay_frequency", e.value)
                          }
                          styles={darkSelectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    )}

                    {employed === "no" && (
                      <div>
                        <label className="block text-gray-300 mb-3 text-sm font-medium">
                          Benefit Type
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="radio"
                              {...register("income.benefit_type")}
                              value="0"
                              defaultChecked
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-gray-200">Unemployment</span>
                          </label>
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="radio"
                              {...register("income.benefit_type")}
                              value="1"
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-gray-200">SSI</span>
                          </label>
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="radio"
                              {...register("income.benefit_type")}
                              value="2"
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-gray-200">Disability</span>
                          </label>
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="radio"
                              {...register("income.benefit_type")}
                              value="3"
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-gray-200">Other</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Skip Button */}
              {["skip", "skip-login"].includes(
                appConfig.onboarding_financials
              ) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex flex-col items-center mb-8"
                >
                  <button
                    type="button"
                    onClick={doSkip}
                    className="mt-3 text-gray-400 hover:text-gray-300 text-sm"
                  >
                    Maybe Later
                  </button>
                </motion.div>
              )}

              {/* Expenses Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-8"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <Wallet size={20} className="text-red-400" />
                    </div>
                    <h2 className="text-2xl font-semibold text-white">
                      Expenses
                    </h2>
                  </div>
                  <p className="text-gray-400 text-sm">Estimates are OK!</p>
                </div>

                <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700">
                    <span className="text-gray-400">Total Expenses:</span>
                    <span className="text-2xl font-bold text-red-400">
                      {formatter.format(expenseTotal)}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {expenseFields.map((field, index) => {
                      const Icon = field.icon;
                      return (
                        <motion.div
                          key={field.name}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.05 * index }}
                        >
                          <label className="block text-gray-300 mb-2 text-sm font-medium">
                            <div className="flex items-center gap-2 mb-1">
                              <Icon size={16} className="text-gray-500" />
                              {field.label}
                            </div>
                            {field.hint && (
                              <span className="text-gray-500 text-xs">
                                {field.hint}
                              </span>
                            )}
                          </label>
                          <input
                            type="text"
                            placeholder="$0.00"
                            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            {...register(`expenses.${field.name}`)}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>

              {/* Summary Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <PieChart size={20} className="text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white">Summary</h2>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Chart Card */}
                  <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Expense Breakdown
                    </h3>
                    <div className="flex items-center justify-center">
                      {chartData.length > 0 ? (
                        <Donut
                          data={chartData}
                          externalRadius={120}
                          internalRadius={40}
                        />
                      ) : (
                        <div className="text-gray-500 text-center py-8">
                          <PieChart
                            size={48}
                            className="mx-auto mb-2 opacity-50"
                          />
                          <p>Enter expenses to see breakdown</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 text-center">
                      <p className="text-gray-400 text-sm">Total Expenses</p>
                      <p className="text-2xl font-bold text-white">
                        {formatter.format(expenseTotal)}
                      </p>
                    </div>
                  </div>

                  {/* Available Funds Card */}
                  <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Available Funds
                    </h3>

                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Monthly Income</span>
                        <span className="text-green-400 font-semibold">
                          {formatter.format(incomeTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Monthly Expenses</span>
                        <span className="text-red-400 font-semibold">
                          {formatter.format(expenseTotal)}
                        </span>
                      </div>
                      <div className="pt-4 border-t border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300 font-medium">
                            Available
                          </span>
                          <span
                            className={`text-2xl font-bold ${availableFunds >= 0 ? "text-green-400" : "text-red-400"}`}
                          >
                            {formatter.format(availableFunds)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="flex justify-between text-sm text-gray-400 mb-2">
                        <span>Expense Ratio</span>
                        <span>{Math.round(expenseRatio * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            expenseRatio < 0.5
                              ? "bg-green-500"
                              : expenseRatio < 0.75
                                ? "bg-yellow-500"
                                : expenseRatio < 1
                                  ? "bg-orange-500"
                                  : "bg-red-500"
                          }`}
                          style={{
                            width: `${Math.min(expenseRatio * 100, 100)}%`,
                          }}
                        />
                      </div>
                      {expenseRatio > 0 && (
                        <p
                          className={`mt-2 text-sm ${
                            expenseRatio < 0.5
                              ? "text-green-400"
                              : expenseRatio < 0.75
                                ? "text-yellow-400"
                                : expenseRatio < 1
                                  ? "text-orange-400"
                                  : "text-red-400"
                          }`}
                        >
                          {expenseRatio < 0.5 &&
                            "You're doing great! Keep up the good work."}
                          {expenseRatio >= 0.5 &&
                            expenseRatio < 0.75 &&
                            "Good progress, but consider reducing some expenses."}
                          {expenseRatio >= 0.75 &&
                            expenseRatio < 1 &&
                            "Getting close to your limit. Review your expenses."}
                          {expenseRatio >= 1 &&
                            "You're over budget. Time to cut back on spending."}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Submit Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col items-center"
              >
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      Save Financial Information
                    </>
                  )}
                </button>
              </motion.div>
              </form>
            </div>
          </LayoutAuth>
        )}
      </div>
    </div>
  );
};

export default Questionnaire;
